import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const clampPercent = value => Math.min(85, Math.max(1, Number(value) || 0));
const money = value => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const FAQ = [
  ['What is gross margin on a fence job?', 'Gross margin is the percentage of the selling price left after the direct costs entered here. If a job sells for $10,000 and the entered direct costs total $6,500, gross profit is $3,500 and gross margin is 35%.'],
  ['Is markup the same as margin?', 'No. Markup compares gross profit to cost, while margin compares gross profit to selling price. A 50% markup on cost does not equal a 50% gross margin.'],
  ['What costs should I include?', 'Use the direct costs you want to evaluate consistently, such as materials, field labor, equipment, disposal, subcontractors or other job-specific costs. This calculator does not decide which accounting categories belong in your business.'],
  ['What is a good profit margin for a fence contractor?', 'There is no universal target. Labor model, overhead, market, warranty exposure, material mix and growth strategy all matter. Use a target that reflects your own cost structure and business goals rather than treating a generic percentage as a guarantee.'],
];

function marginBand(margin, target) {
  if (!Number.isFinite(margin)) return { label: 'Add a selling price and costs', detail: 'Profit Guard will calculate the job economics as you enter the numbers.', icon: 'Gauge' };
  if (margin < 0) return { label: 'Below direct cost', detail: 'The selling price is below the direct costs entered here.', icon: 'TriangleAlert' };
  const gap = margin - target;
  if (gap < -10) return { label: 'Well below your target', detail: Math.abs(gap).toFixed(1) + ' percentage points below the gross-margin target you selected. Review price, scope and direct costs before quoting.', icon: 'ShieldAlert' };
  if (gap < 0) return { label: 'Below your target', detail: Math.abs(gap).toFixed(1) + ' percentage points below your selected gross-margin target.', icon: 'Gauge' };
  if (gap <= 5) return { label: 'At or above your target', detail: 'The entered price and direct costs are within 5 percentage points above the gross-margin target you selected.', icon: 'ShieldCheck' };
  return { label: 'Above your target', detail: gap.toFixed(1) + ' percentage points above your selected gross-margin target. Confirm scope, overhead and risk before treating the excess as available profit.', icon: 'ShieldCheck' };
}

export default function FenceProfitMarginCalculator() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const initialPrice = searchParams.get('price') || '';
  const initialMaterials = searchParams.get('materials') || '';
  const initialLabor = searchParams.get('labor') || '';
  const initialEquipment = searchParams.get('equipment') || '';
  const initialOther = searchParams.get('other') || '';
  const sourceLabel = searchParams.get('job') || '';
  const [price, setPrice] = useState(initialPrice);
  const [materials, setMaterials] = useState(initialMaterials);
  const [labor, setLabor] = useState(initialLabor);
  const [equipment, setEquipment] = useState(initialEquipment);
  const [other, setOther] = useState(initialOther);
  const [targetMargin, setTargetMargin] = useState('35');
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => {
    const sale = Number(price);
    const costInputs = [materials, labor, equipment, other];
    const costs = costInputs.map(value => Number(value) || 0);
    const costsEntered = costInputs.some(value => String(value).trim() !== '');
    const totalCost = costs.reduce((sum, value) => sum + value, 0);
    const validSale = Number.isFinite(sale) && sale > 0;
    const grossProfit = validSale && costsEntered ? sale - totalCost : null;
    const grossMargin = validSale && costsEntered ? (grossProfit / sale) * 100 : null;
    const markup = totalCost > 0 && grossProfit !== null ? (grossProfit / totalCost) * 100 : null;
    const target = clampPercent(targetMargin);
    const suggestedPrice = costsEntered && totalCost > 0 && target < 100 ? totalCost / (1 - target / 100) : null;
    const gap = suggestedPrice !== null && validSale ? suggestedPrice - sale : null;
    return { sale, totalCost, costsEntered, grossProfit, grossMargin, markup, target, suggestedPrice, gap };
  }, [price, materials, labor, equipment, other, targetMargin]);

  const band = marginBand(result.grossMargin, result.target);

  const reset = () => {
    setPrice(initialPrice);
    setMaterials(initialMaterials);
    setLabor(initialLabor);
    setEquipment(initialEquipment);
    setOther(initialOther);
    setTargetMargin('35');
    setCopied(false);
  };

  const copyResult = async () => {
    const summary = [
      'PicketOps Profit Guard',
      sourceLabel ? 'Estimate: ' + sourceLabel : null,
      'Selling price: ' + (result.sale > 0 ? money(result.sale) : 'Not entered'),
      'Direct costs entered: ' + money(result.totalCost),
      'Gross profit: ' + (result.grossProfit === null ? 'Not available' : money(result.grossProfit)),
      'Gross margin: ' + (result.grossMargin === null ? 'Not available' : result.grossMargin.toFixed(1) + '%'),
      'Markup on entered costs: ' + (result.markup === null ? 'Not available' : result.markup.toFixed(1) + '%'),
      'Target margin: ' + result.target.toFixed(0) + '%',
      'Selling price for target: ' + (result.suggestedPrice === null ? 'Not available' : money(result.suggestedPrice)),
      '',
      'Educational calculator only. Review your full cost structure, overhead and job risk before quoting.',
    ].filter(Boolean).join('\n');

    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const workspaceTarget = !isAuthenticated
    ? '/contractor/signup'
    : sourceLabel.startsWith('PROP-')
      ? '/proposals'
      : sourceLabel.startsWith('EST-')
        ? '/estimates'
        : sourceLabel.toLowerCase().includes('labor plan')
          ? '/jobs'
          : '/price-book';
  const fromEstimateCalculator = sourceLabel === 'Fence estimate calculator';

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <a href="#profit-calculator" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-3 focus:text-primary-foreground">
        Skip to profit calculator
      </a>

      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span>
            <span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span>
            <span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span>
          </span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link>
          <span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Fence profit margin calculator</span>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16 space-y-10">
        <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-md)">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free Profit Guard · No signup required</p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">Fence job profit margin calculator</h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
                Enter the selling price and direct job costs to see gross profit, gross margin, markup and the price required to hit your target margin.
              </p>
              {sourceLabel && (
                <div className="mt-5 inline-flex items-center gap-2 rounded-md border border-primary/25 bg-primary/10 px-3 py-2 text-sm">
                  <ApperIcon name="FileText" size={16} className="text-primary" />
                  Profit check for {sourceLabel}
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild><a href="#profit-calculator">Run the calculation <ApperIcon name="ArrowDown" size={16} /></a></Button>
                <Button variant="outline" asChild><Link to="/contractor-revenue-scorecard">Check my revenue leaks</Link></Button>
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Margin vs. markup</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-border bg-background p-4">
                  <p className="text-sm font-semibold">Margin</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">Gross profit ÷ selling price</p>
                </div>
                <div className="rounded-xl border border-border bg-background p-4">
                  <p className="text-sm font-semibold">Markup</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">Gross profit ÷ direct cost</p>
                </div>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                They are not interchangeable. Profit Guard shows both so a pricing decision is less likely to be based on the wrong percentage.
              </p>
            </div>
          </div>
        </section>

        <section id="profit-calculator" aria-labelledby="calculator-heading" className="scroll-mt-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-5">
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Job economics</p>
                <h2 id="calculator-heading" className="mt-2 text-2xl font-heading font-semibold">Enter the numbers you want to test.</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Nothing entered here is saved to PicketOps.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="profit-sale-price">Selling price</Label>
                <Input id="profit-sale-price" type="number" min="0" step="0.01" inputMode="decimal" value={price} onChange={event => setPrice(event.target.value)} placeholder="0.00" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ['materials', 'Materials', materials, setMaterials, '0.00'],
                  ['labor', 'Direct labor', labor, setLabor, '0.00'],
                  ['equipment', 'Equipment / disposal', equipment, setEquipment, '0.00'],
                  ['other', 'Other direct cost', other, setOther, '0.00'],
                ].map(([id, label, value, setter, placeholder]) => (
                  <div key={id} className="space-y-2">
                    <Label htmlFor={'profit-' + id}>{label}</Label>
                    <Input id={'profit-' + id} type="number" min="0" step="0.01" inputMode="decimal" value={value} onChange={event => setter(event.target.value)} placeholder={placeholder} />
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="profit-target">Target gross margin</Label>
                  <span className="text-sm font-semibold tabular-nums">{clampPercent(targetMargin).toFixed(0)}%</span>
                </div>
                <p className="text-xs text-muted-foreground">35% is loaded only as an illustrative starting value—not a recommended target. Adjust it to your own cost structure and goals.</p>
                <Input id="profit-target" type="range" min="1" max="85" step="1" value={clampPercent(targetMargin)} onChange={event => setTargetMargin(event.target.value)} />
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" max="85" step="1" inputMode="decimal" value={targetMargin} onChange={event => setTargetMargin(event.target.value)} aria-label="Target gross margin percentage" className="w-28" />
                  <span className="text-sm text-muted-foreground">% target</span>
                </div>
              </div>

              {!result.costsEntered && price && <p className="rounded-lg border border-warning-border bg-warning-muted p-3 text-sm text-warning">Add at least one direct-cost input before interpreting gross profit or margin.</p>}
              <Button type="button" variant="outline" onClick={reset}><ApperIcon name="RotateCcw" size={16} />Reset calculator</Button>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-sm)">
              <div className="p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <span className="flex size-11 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name={band.icon} size={21} /></span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">Profit Guard</p>
                    <h2 className="mt-1 text-2xl font-heading font-semibold">{band.label}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{band.detail}</p>
                  </div>
                </div>

                <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-4">
                  {[
                    ['Direct costs', result.costsEntered ? money(result.totalCost) : '—'],
                    ['Gross profit', result.grossProfit === null ? '—' : money(result.grossProfit)],
                    ['Gross margin', result.grossMargin === null ? '—' : result.grossMargin.toFixed(1) + '%'],
                    ['Markup', result.markup === null ? '—' : result.markup.toFixed(1) + '%'],
                  ].map(([label, value]) => (
                    <div key={label} className="bg-background p-4">
                      <dt className="text-xs text-muted-foreground">{label}</dt>
                      <dd className="mt-1 text-xl font-bold tabular-nums">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="border-t border-border bg-primary/5 p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Target price check</p>
                <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Selling price for a {result.target.toFixed(0)}% gross margin on the entered direct costs</p>
                    <p className="mt-1 text-4xl font-bold tabular-nums">{result.suggestedPrice === null ? '—' : money(result.suggestedPrice)}</p>
                  </div>
                  {result.gap !== null && (
                    <p className="text-sm font-semibold tabular-nums">
                      {Math.abs(result.gap) < 0.01
                        ? 'Current price matches the target.'
                        : result.gap > 0
                          ? money(result.gap) + ' above current price'
                          : money(Math.abs(result.gap)) + ' below current price'}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <div className="flex flex-wrap gap-3">
              <Button type="button" onClick={copyResult} disabled={result.grossProfit === null}>
                <ApperIcon name={copied ? 'Check' : 'Copy'} size={16} />{copied ? 'Copied' : 'Copy profit check'}
              </Button>
              {fromEstimateCalculator && <Button variant="outline" asChild><Link to="/fence-estimate-calculator"><ApperIcon name="ArrowLeft" size={16}/>Back to estimate calculator</Link></Button>}
              <Button variant="outline" asChild>
                <Link to={workspaceTarget}>{isAuthenticated ? (workspaceTarget==='/proposals'?'Open proposals':workspaceTarget==='/estimates'?'Open estimates':'Open PicketOps price book') : 'Create account'} <ApperIcon name="ArrowRight" size={16} /></Link>
              </Button>
            </div>

            <p className="text-xs leading-relaxed text-muted-foreground">
              Educational planning tool only. Results depend entirely on the costs you enter and do not include taxes, overhead, financing, warranty exposure, callbacks, owner compensation or other costs unless you include them yourself.
            </p>
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-4">
          {[
            ['ScanLine', 'Use the same cost definition every time', 'A margin number is only comparable when you classify job costs consistently from estimate to estimate.'],
            ['ShieldAlert', 'Review scope before lowering price', 'A weak margin may come from missed labor, removal, access or material requirements—not just an expensive selling price.'],
            ['ChartNoAxesCombined', 'Watch margin and close rate together', 'Higher pricing can protect margin but also change conversion. The useful target is a profitable process, not one percentage in isolation.'],
          ].map(([icon, title, detail]) => (
            <Card key={title}>
              <CardContent className="p-5">
                <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name={icon} size={19} /></span>
                <h2 className="mt-4 text-lg font-heading font-semibold">{title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{detail}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section aria-labelledby="profit-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="profit-faq-heading" className="text-2xl font-heading font-semibold">Fence contractor margin questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">
            {FAQ.map(([question, answer]) => (
              <details key={question} className="py-4">
                <summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-primary/25 bg-card p-6 sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Next step</p>
          <h2 className="mt-2 text-3xl font-heading font-semibold">Turn the calculation into a repeatable estimating process.</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            PicketOps keeps fence rates, estimates, quote follow-up dates, scheduling and job records together. Profit Guard gives you the math; the Revenue OS keeps the next operational action visible.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild><Link to={workspaceTarget}>{isAuthenticated ? 'Open price book' : 'Create contractor account'} <ApperIcon name="ArrowRight" size={16} /></Link></Button>
            <Button variant="outline" asChild><Link to="/fence-estimate-follow-up">Fence estimate follow-up guide</Link></Button>
          </div>
        </section>
      </main>
    </div>
  );
}

export const route = { path: '/fence-profit-margin-calculator', layout: 'public' };
